import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  memo,
  useEffect,
} from 'react';
import { StyleSheet, View } from 'react-native';

import Utils from '../../../utils';
import TextUtils from '../../../textUtils';
import { __WEB__, COLOR, THEME } from '../../../constants/styles';

import Select from './select';
import TextInput from './text_input_plain';
import ValidatedComponent from './validated_comp';

const PHONE_PREFIXES = ['+1', '+81', '+82', '+39', '+44', '+971', '+974', '+966'];
const PHONE_PREFIX_OPTIONS = PHONE_PREFIXES.map((value) => Utils.optionMapper(value));
const PHONE_PREFIX_MATCH_ORDER = PHONE_PREFIXES.slice().sort((a, b) => b.length - a.length);
const DEFAULT_PHONE_PREFIX = '+1';

function splitPhoneValue(value, useDefaultPrefix) {
  const trimmedValue = value?.trim();
  if (!trimmedValue) {
    return {
      phoneCountryCode: useDefaultPrefix ? DEFAULT_PHONE_PREFIX : null,
      phoneNumber: null,
    };
  }

  const matchedPhonePrefix = PHONE_PREFIX_MATCH_ORDER.find((prefix) =>
    trimmedValue.startsWith(prefix)
  );
  if (!matchedPhonePrefix) {
    return {
      phoneCountryCode: useDefaultPrefix ? DEFAULT_PHONE_PREFIX : null,
      phoneNumber: trimmedValue,
    };
  }

  return {
    phoneCountryCode: matchedPhonePrefix,
    phoneNumber: trimmedValue.slice(matchedPhonePrefix.length).trim() || null,
  };
}

function getPhoneValue(phoneCountryCode, phoneNumber) {
  return [phoneCountryCode, phoneNumber?.trim()].filter(Boolean).join('');
}

const PhoneInput = forwardRef((props, ref) => {
  const editable = props.editable !== false;
  const readOnly = props.readOnly === true || !editable;
  const autoCorrect = props.autoCorrect !== false;

  const validatedRef = useRef(null);
  const inputRef = useRef(null);
  const phoneParts = splitPhoneValue(props.value, !readOnly);
  const phoneCountryCode = phoneParts.phoneCountryCode;
  const phoneNumber = phoneParts.phoneNumber;
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (props.readOnly || !props.value) return;

    const rawValue = props.value.trim();
    if (!rawValue || rawValue.startsWith('+')) return;

    const { phoneCountryCode, phoneNumber } = splitPhoneValue(rawValue, true);
    const normalizedNumber =
      phoneCountryCode === '+1' && phoneNumber?.startsWith('1')
        ? phoneNumber.slice(1)
        : phoneNumber;

    props.onChangeText?.(getPhoneValue(phoneCountryCode, normalizedNumber));
  }, [props.value, props.onChangeText, props.readOnly]);

  const focus = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const validate = useCallback(() => {
    return validatedRef.current?.validate();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      focus,
      validate,
    }),
    [focus, validate]
  );

  const handleFocus = useCallback(() => {
    setActive(true);
    props.onFocus?.();
  }, [props.onFocus]);

  const handleBlur = useCallback(() => {
    setActive(false);
    props.onBlur?.();
  }, [props.onBlur]);

  const handlePhoneCountryCode = useCallback(
    (value) => {
      props.onChangeText?.(getPhoneValue(value, phoneNumber));
    },
    [phoneNumber, props.onChangeText]
  );

  const handlePhoneNumber = useCallback(
    (value) => {
      let newPhone = value;
      let newCountryCode = phoneCountryCode;
      // handle if they start typing the + and the country code, or if they paste in a full phone number with country code
      if (value.startsWith('+')) {
        const phoneParts = splitPhoneValue(value, true);
        newPhone = phoneParts.phoneNumber;
        newCountryCode = phoneParts.phoneCountryCode;
        // this handles auto complete in US, or if they paste in a full US number starting with 1
      } else if (value.startsWith('1') && phoneCountryCode === '+1') {
        newPhone = value.slice(1);
      }

      props.onChangeText?.(getPhoneValue(newCountryCode, newPhone));
    },
    [phoneCountryCode, props.onChangeText]
  );

  const webMode = props.webMode !== false;
  return (
    <ValidatedComponent
      ref={validatedRef}
      {...props}
      required={props.required === true}
      value={props.value}
      validateFunc={!readOnly ? Utils.validate.phone : null}
      style={[styles.container, props.containerStyle, active ? { zIndex: 1000 } : null]}
    >
      <View style={[styles.phoneFieldRow, readOnly ? styles.phoneFieldRowReadOnly : null]}>
        {!(readOnly && !phoneCountryCode) && (
          <Select
            value={phoneCountryCode}
            onSelect={handlePhoneCountryCode}
            onBlur={handleBlur}
            onFocus={handleFocus}
            options={PHONE_PREFIX_OPTIONS}
            editable={!readOnly}
            disabled={props.disabled}
            webMode={webMode}
            menuPlacement={props.menuPlacement || 'auto'}
            noLabel={true}
            containerStyle={[styles.phonePrefixContainer, props.prefixContainerStyle]}
            nativeContainerStyle={[
              styles.phonePrefixContainer,
              styles.nativePrefixContainer,
              props.prefixNativeContainerStyle,
            ]}
            valueStyle={[styles.phonePrefixValue, props.prefixValueStyle]}
            valueContainerStyle={[
              styles.phonePrefixValueContainer,
              props.prefixValueContainerStyle,
            ]}
          />
        )}
        <View
          style={[
            styles.phoneNumberContainer,
            props.disabled ? styles.phoneNumberContainerDisabled : null,
            !__WEB__ || !webMode ? styles.phoneNumberContainerMobile : null,
            props.inputContainerStyle,
          ]}
        >
          <TextInput
            ref={inputRef}
            style={[THEME.input, styles.input, props.inputStyle]}
            onBlur={__WEB__ ? handleBlur : null}
            onFocus={__WEB__ ? handleFocus : null}
            value={phoneNumber == null ? '' : phoneNumber}
            maxLength={props.maxLength}
            multiline={props.multiline}
            numberOfLines={props.numberOfLines}
            placeholder={props.placeholder || TextUtils.ac(props.label)}
            placeholderTextColor={COLOR.textLight}
            autoCorrect={autoCorrect}
            autoComplete={__WEB__ ? props.label : null}
            autoCapitalize={props.autoCapitalize || 'none'}
            keyboardType={props.keyboardType || 'phone-pad'}
            returnKeyType={__WEB__ ? 'done' : props.returnKeyType || 'next'}
            selectTextOnFocus={props.selectTextOnFocus !== false}
            clearButtonMode="while-editing"
            underlineColorAndroid="transparent"
            onChangeText={handlePhoneNumber}
            onSubmitEditing={props.onSubmitEditing}
            editable={!readOnly}
            testID={props.testID || props.label}
          />
        </View>
      </View>
    </ValidatedComponent>
  );
});

export default memo(PhoneInput);

const styles = StyleSheet.create({
  container: {},
  phoneFieldRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    zIndex: 1,
  },
  phoneFieldRowReadOnly: {
    marginBottom: 10,
  },
  phonePrefixContainer: {
    width: 92,
    marginRight: 12,
    textAlign: 'right',
    marginTop: 0,
    marginBottom: 0,
    paddingBottom: 0,
  },
  nativePrefixContainer: {},
  phonePrefixValueContainer: {
    alignItems: 'flex-end',
  },
  phonePrefixValue: {
    marginRight: 12,
    textAlign: 'right',
  },
  phoneNumberContainer: {
    flex: 1,
    height: 43,
    borderBottomWidth: 1,
    borderBottomColor: COLOR.text,
    marginBottom: 0,
    paddingBottom: 0,
  },
  phoneNumberContainerDisabled: {
    ...THEME.inputContainerDisabled,
  },
  phoneNumberContainerMobile: {},
  input: {},
});
